import {
  Flex,
  Skeleton,
  Table,
  Button,
  Heading,
  Separator,
  ExternalLinkIcon,
  Text,
} from '@buffer-mono/popcorn'

import { type FragmentOf, graphql, readFragment } from '~publish/graphql'
import { useQuery } from '@apollo/client'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { formatCurrency } from './PlanContent'

import style from '../Settings.module.css'
import styleInvoices from './Invoices.module.css'

import {
  useGetPortalSession,
  PortalOptions,
} from '../hooks/useGetPortalSession'
import { getSettingsUrl } from '../util/getSettingsUrl'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'
import { AddressAndTax_Billing } from './BillingInformation'

const InvoicesQuery = graphql(/* GraphQL */ `
  query Invoices($organizationId: String!, $first: Int, $after: String) {
    invoicesByOrganization(
      organizationId: $organizationId
      first: $first
      after: $after
    ) {
      edges {
        node {
          id
          amount
          status
          downloadUrl
          date
          currency
        }
      }
      pageInfo {
        hasNextPage
        startCursor
        endCursor
      }
    }
  }
`)

// from 2025-03-31T17:27:53.000Z to July 17, 2024 style
function formatDate(date: string): string {
  const dateObj = new Date(date)
  return dateObj.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

export const Invoices = ({
  billingData,
}: {
  billingData: FragmentOf<typeof AddressAndTax_Billing> | null
}): JSX.Element => {
  const organizationId = useOrganizationId()
  const fragmentData = billingData
    ? readFragment(AddressAndTax_Billing, billingData)
    : null
  const { gateway, previousGateway } = fragmentData ?? {}
  const gatewayId = gateway?.gatewayId
  const { data, loading, error } = useQuery(InvoicesQuery, {
    variables: {
      organizationId: organizationId ?? '',
      first: 3,
    },
    skip: !organizationId,
  })

  const { getCustomerPortalSession, loading: portalSessionLoading } =
    useGetPortalSession({
      portalOption: PortalOptions.MAIN_PAGE,
    })

  const invoices = data?.invoicesByOrganization.edges
    .map((edge) => edge?.node)
    .filter((invoice) => invoice !== undefined)

  const trackPortalSession = ({ ctaButton }: { ctaButton: string }): void => {
    BufferTracker.billingPortalSessionCreated({
      organizationId: organizationId ?? null,
      customerId: gatewayId,
      ctaApp: 'publish',
      ctaView: 'billing',
      ctaLocation: 'invoices',
      ctaButton,
      ctaVersion: '1',
    })
  }

  if (!invoices || invoices.length === 0 || error) {
    return <></>
  }

  return (
    <>
      <Separator className={style.fullWidth} />
      <Skeleton show={loading}>
        <Flex
          gap="xs"
          direction="row"
          align="center"
          justify="between"
          fullWidth
        >
          <Heading size="medium">Invoices &amp; Receipts</Heading>
          <Button
            variant="secondary"
            onClick={(): void => {
              trackPortalSession({ ctaButton: 'viewAll' })
              getCustomerPortalSession(getSettingsUrl())
            }}
            disabled={portalSessionLoading}
          >
            View All
            <ExternalLinkIcon />
          </Button>
        </Flex>
      </Skeleton>
      {previousGateway?.gatewayType === 'stripe' &&
        gateway?.gatewayType === 'revenuecat' && (
          <Flex
            direction="column"
            align="center"
            gap="lg"
            fullWidth
            className={styleInvoices.invoicesInfo}
          >
            <Text size="md" align="left" lineHeight="regular">
              Your current invoices can be accessed through your app store, and
              your previous web-based invoices are available below.
            </Text>
          </Flex>
        )}
      <Skeleton show={loading}>
        <Table fullWidth>
          <Table.Header>
            <Table.HeaderCell>Date</Table.HeaderCell>
            <Table.HeaderCell>Amount</Table.HeaderCell>
            <Table.HeaderCell></Table.HeaderCell>
          </Table.Header>
          <Table.Body>
            {invoices?.map((invoice) => (
              <Table.Row key={invoice.id}>
                <Table.Cell>{formatDate(invoice.date)}</Table.Cell>
                <Table.Cell>${formatCurrency(invoice.amount)}</Table.Cell>
                <Table.Cell align="right">
                  <Button
                    size="small"
                    variant="tertiary"
                    as="a"
                    href={invoice.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: 'var(--color-text-neutral)',
                      fontWeight: 'medium',
                      display: 'inline-flex',
                    }}
                  >
                    View
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Skeleton>
    </>
  )
}

export default Invoices
